// Greedy — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as dp2d.js: concept → internals →
// applied template → correctness (exchange argument) → errors → complexity → beginner drills → cheatsheet → playground.
// Reuses the existing 'pointer-diagram' section type for its visual — no new diagram component needed.

import greedyDrills from './greedyDrills';
const drill = id => greedyDrills.find(d => d.id === id);

const greedy = {
  key: 'Greedy',
  tagline: 'Make the locally best choice at every step, commit to it forever, and trust that it adds up to the global optimum.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Greedy',
      type: 'text',
      body: [
        "Greedy is the discipline of making the locally best choice at each step, and NEVER reconsidering it — no backtracking, no revisiting, no lookahead beyond the current decision. Where 1-D and 2-D Dynamic Programming carefully weigh every possible path through a table of subproblems, greedy commits immediately, on the spot.",
        "That commitment is what makes greedy fast — usually a single linear pass — but it's also exactly what makes greedy risky: the algorithm never gets a chance to reconsider an earlier decision once a later one reveals it was suboptimal. Everything in this pattern hinges on knowing WHEN that risk is actually safe.",
      ],
    },
    {
      id: 'why',
      title: 'Why Greedy?',
      type: 'text',
      body: [
        "Greedy is dramatically faster than DP or backtracking for the problems it actually applies to — usually O(n) or O(n log n), with O(1) extra state, versus a full table of subproblems or an exponential search tree.",
        "But it only produces the CORRECT answer for problems that have a specific property: making the locally optimal choice at every step must never rule out reaching the true global optimum. Recognizing whether a problem actually has this property — and being able to justify it, at least informally — is the entire skill of this pattern. Get it wrong, and a greedy solution returns a plausible-looking WRONG answer, with no error, no exception, nothing to signal the mistake.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Greedy Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Hiker Who Never Looks Back, and Making Change With the Wrong Coins',
      body: [
        "Picture a hiker who, at every fork in the trail, simply takes whichever path currently looks like it climbs highest or extends furthest — and never turns back to reconsider. This works beautifully when a locally-better view genuinely can never hurt a later decision: in Jump Game, tracking the single farthest point reachable so far can never make a future choice worse, since every position reachable from a smaller frontier is also reachable from a larger one.",
        "Now picture making change for an amount using the biggest coin that fits, over and over, until done. With familiar denominations like 25, 10, 5, 1, this greedy strategy happens to always use the fewest coins. But with coins [1, 3, 4] making 6: greedy grabs a 4, then a 1, then a 1 — three coins — while the true optimum is 3 + 3, just two coins. The greedy choice \"looked\" locally correct at every step, and was still wrong. This is the cautionary half of the picture: greedy is not automatically correct just because a locally-best choice exists — it must be PROVEN correct for the specific problem at hand.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Greedy Pass',
      type: 'pointer-diagram',
      body: [
        "A greedy algorithm typically needs just ONE small piece of running state, updated as it scans forward — no table, no branching search. Here, a single \"farthest reach\" pointer is all Jump Game needs to decide reachability for the entire array.",
        "By the end of the scan, farthest has climbed to (or past) the last index — confirming the array is fully traversable, all from one rolling value updated during a single pass.",
      ],
      demo: {
        values: [2, 3, 1, 1, 4],
        pointers: [{ index: 4, label: 'farthest reach (≥ here)', color: '#7F00FF' }],
        note: 'farthest actually computes to 8 for this array (past the last index) — the pointer rests at the last real cell to show "at least this far, and beyond."',
      },
    },
    {
      id: 'template',
      title: 'The Universal Greedy Recipe',
      type: 'code',
      code:
`int solveGreedy(int[] nums) {
    /* running state that captures everything needed
       to make the next locally-best decision */
    int state = /* initial value */;

    for (int i = 0; i < nums.length; i++) {
        if (/* current state already proves failure or determines the answer */) {
            return /* fail fast — no point continuing */;
        }
        state = /* update state to reflect the locally best choice available now */;
    }
    return /* derive the final answer from state */;
}`,
      note: 'The entire pattern is variations on this shape: a small amount of running state, updated once per element, with an early-exit check whenever the state already proves the outcome. The hard part is never the code — it\'s correctly identifying what that one piece of state needs to be, and proving updating it greedily is safe.',
    },
    {
      id: 'jumpgametemplate',
      title: 'Applied: The Farthest-Reach Recurrence',
      type: 'code',
      code:
`boolean canJump(int[] nums) {
    int farthest = 0;
    for (int i = 0; i < nums.length; i++) {
        if (i > farthest) return false;         // this index was never reachable
        farthest = Math.max(farthest, i + nums[i]);
    }
    return true;
}`,
      note: 'The running state is a single integer: the best reach discovered so far. Notice what\'s NOT tracked — which specific sequence of jumps got here, or how many jumps were used. None of that matters for answering "is this feasible at all," which is exactly why greedy collapses an entire DP table down to one rolling value.',
    },
    {
      id: 'exchangeargument',
      title: 'Why Trust a Greedy Choice? The Exchange Argument',
      type: 'text',
      heading: 'Proving a Greedy Strategy, Not Just Observing It',
      body: [
        "The standard way to justify a greedy algorithm is an EXCHANGE ARGUMENT: take any hypothetical optimal solution that made a different (non-greedy) choice at some step, and show that swapping in the greedy choice instead can only ever match or improve that solution — never make it worse. If that swap is always safe, the greedy choice is provably at least as good as any alternative, at every single step.",
        "For Jump Game specifically: suppose some solution reaches index i without ever tracking the single farthest position reachable so far. Swapping in \"always know and use the farthest reach discovered so far\" can only help — a larger reachable frontier can never make a LATER decision worse, since every position reachable from a smaller frontier is also reachable from a larger one. That's what makes the farthest-tracking greedy provably correct here, not just observed to work on a few examples.",
        "Contrast this with the coin-change counterexample from the earlier analogy: swapping \"always take the biggest coin that fits\" into an optimal solution for coins [1, 3, 4] making 6 does NOT preserve optimality — 4+1+1 is worse than 3+3. The exchange argument FAILS for that problem, which is exactly the signal that greedy isn't the right tool there — dynamic programming is.",
      ],
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Assuming greedy works without checking (or being able to articulate) why',
          why: 'A greedy algorithm can look completely reasonable, run without errors, and return a plausible number — while being subtly wrong on inputs that weren\'t tested. Unlike an off-by-one bug, a wrong greedy choice doesn\'t crash; it silently produces a suboptimal or incorrect answer.',
          fix: 'Before implementing, try to state — even informally — why the locally best choice can never hurt the final answer (an exchange argument). If that reasoning doesn\'t hold, or a counterexample is easy to construct, greedy is the wrong tool; reach for DP instead.',
        },
        {
          mistake: 'Confusing "looks locally best" with "is provably safe"',
          why: 'Many WRONG greedy strategies still look locally reasonable — e.g. in Jump Game II, always jumping as far as possible in a single step (rather than tracking the farthest reachable within the CURRENT jump\'s range) can overshoot a jump boundary and miscount the minimum jumps needed.',
          fix: 'Be precise about which quantity the greedy choice actually tracks — often it\'s not "the single best option right now," but "the best option reachable within the current commitment\'s boundary" (Jump Game II\'s currentEnd).',
        },
        {
          mistake: 'Forgetting the fail-fast check',
          why: 'For feasibility problems (can this be done at all?), skipping an early "is progress still possible?" check lets the loop keep running past a point where the answer is already determined to be false — sometimes causing wasted work, sometimes an out-of-bounds access.',
          fix: 'The moment the greedy state proves the goal is unreachable (e.g. the current index has outrun the farthest reach), return immediately — don\'t let the loop continue.',
        },
        {
          mistake: 'Not resetting accumulated state after a failure point (Gas Station-style problems)',
          why: 'In circular-tour problems like Gas Station, once the running tank goes negative at some station, EVERY station between the old candidate start and that station is also provably invalid as a starting point. Forgetting to reset the candidate start (only resetting the tank) silently keeps stale, invalid state around.',
          fix: 'When a greedy running total goes negative, reset both the running total AND the candidate start index to right after the failure point — the accumulated deficit up to that point can never be recovered by starting earlier within the same stretch.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Feasibility check (Jump Game)', time: 'O(n)', note: 'A single pass, tracking one rolling "farthest reach" value.' },
        { op: 'Minimum-step count (Jump Game II)', time: 'O(n)', note: 'One pass, expanding one reachable "level" of jumps at a time.' },
        { op: 'Circular-tour feasibility (Gas Station)', time: 'O(n)', note: 'One pass, resetting the candidate start whenever the running tank goes negative.' },
        { op: 'Partition / interval-merge greedy (Partition Labels)', time: 'O(n)', note: 'A fixed-size (26-entry) last-occurrence table, built once, then a single pass.' },
        { op: 'Sort-first greedy (Assign Cookies, Candy)', time: 'O(n log n)', note: 'Dominated by the initial sort; the greedy pass itself is O(n).' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for Greedy when you see…',
      body: [
        'A problem asking for a single optimal value (min/max/feasible), where a locally-best choice at each step can be argued — via an exchange argument — to never rule out the global optimum.',
        'Sorting first, then scanning once, often exposes a valid greedy order (earliest deadline first, largest value first, and so on).',
        'A DP recurrence attempt that collapses to depending on just ONE simple running value (a farthest reach, a running total, a count) rather than needing to explore multiple branches — that\'s a strong signal a greedy simplification exists.',
        'Avoid greedy — or verify extremely carefully — when a choice can have DELAYED consequences that a purely local view can\'t see; that\'s usually exactly where DP\'s "consider every possibility" is actually required.',
        'When in doubt, try to construct a counterexample for the greedy strategy before trusting it. If none can be found after real effort, and an exchange argument can be sketched, it\'s usually safe.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('best-time-to-buy-sell-stock-ii'),
            drill('assign-cookies'),
            drill('jump-game-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('jump-game-ii'),
            drill('gas-station-drill'),
            drill('partition-labels-drill'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('task-scheduler'),
            drill('candy'),
            drill('remove-duplicate-letters'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with identifying the right piece of running state, and justifying that updating it greedily is safe, before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'The universal recipe: a small amount of running state, updated once per element, with an early-exit the moment that state already determines the outcome.',
        'A greedy strategy needs to be PROVEN safe, not just observed to work on a few examples — try to sketch an exchange argument, or actively hunt for a counterexample, before trusting it.',
        'A larger "farthest reach," "running total," or similar accumulated value can never make a LATER decision worse — that\'s the property that makes many greedy strategies provably correct.',
        'Sorting first is a very common enabler — it exposes a valid greedy processing order that wouldn\'t be visible in the original input order.',
        'When a running total goes negative or fails a feasibility check (Gas Station), reset BOTH the accumulated state and the candidate answer — not just one of them.',
        'If a DP recurrence for a problem turns out to depend on just one simple rolling value instead of a full table, that\'s the signal a greedy simplification exists.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default greedy;
